/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import {
  API_TABLE_EVENT_PAGE_NEXT,
  API_TABLE_EVENT_PAGE_PREVIOUS,
  ApiTable,
  ApiTableFetchOptions,
  ApiTablePageTokens,
  ApiTableProps,
  BUTTON_FORMAT_SOFT,
  BUTTON_FORMAT_TRANSPARENT,
  BUTTON_THEME_DEFAULT,
  Button,
  EmptyState,
  EmptyStateProps,
  ICON_CANCEL_HOLLOW,
  ICON_INFO_WITH_LIST,
  ICON_REFRESH,
  SPINNER_TYPE_REGULAR,
  Spinner,
  TOAST_TYPE_DANGER,
  TOAST_TYPE_SUCCESS,
  TOOLTIP_POSITION_LEFT,
  TableCell,
  TableContent,
  Toast,
  ToastProps,
  Tooltip,
} from '@squiz/sds';
import { getPageToken } from 'apps/console-core/src/Components/SettingList/utils/GetPageToken/GetPageToken';
import { getSlug } from 'apps/console-core/src/Utilities/GetSlug/GetSlug';
import { useToastStore } from 'dxpConsoleCore-mf/State';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { lastValueFrom } from 'rxjs';
import { CONFIGURATION_STRINGS } from '../../../../Constants/Strings.constants';
import {
  EntityJobExecution,
  EntityJobExecutions,
  JobRunnerServiceGetJobExecutionsDto,
  JobRunnerServiceTerminateExecutionDto,
  getJobRunnerService,
} from '../../../../Services/job-runner';
import { JOB_EXECUTIONS_DATA_IDS } from '../../JobExecutions.constants';
import { getFormattedDate } from '../../utils/GetFormattedDate/GetFormattedDate';
import { getFormattedStatus } from '../../utils/GetFormattedStatus/GetFormattedStatus';
import { getFormattedTime } from '../../utils/GetFormattedTime/GetFormattedTime';
import { StartJob } from '../StartJob/StartJob';
import { JOB_EXECUTIONS_LIST_LINK_NEXT, JOB_EXECUTIONS_LIST_LINK_PREVIOUS } from './JobExecutionsList.types';

/**
 * View all available Job Executions.
 */
export const JobExecutionsList = (): JSX.Element => {
  const { t } = useTranslation();

  // Job Runner service
  const jobRunnerService = getJobRunnerService();

  const [jobExecutions, setJobExecutions] = useState<EntityJobExecutions | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { updateActiveToast } = useToastStore();

  // Labels
  const labels = useMemo(() => {
    return {
      cancelJob: t(CONFIGURATION_STRINGS.cancelJob),
      cancelled: t(CONFIGURATION_STRINGS.cancelled),
      context: t(CONFIGURATION_STRINGS.context),
      executionId: t(CONFIGURATION_STRINGS.executionId),
      finished: t(CONFIGURATION_STRINGS.finished),
      jobCancelFailToastDescription1: t(CONFIGURATION_STRINGS.jobStartOrCancelFailToastDescription1),
      jobCancelFailToastDescription2: t(CONFIGURATION_STRINGS.jobCancelFailToastDescription2),
      jobCancelFailToastTitle: t(CONFIGURATION_STRINGS.error),
      jobCancelSuccessToastDescription1: t(CONFIGURATION_STRINGS.jobCancelSuccessToastDescription1),
      jobCancelSuccessToastDescription2: t(CONFIGURATION_STRINGS.jobCancelSuccessToastDescription2),
      jobCancelSuccessToastTitle: t(CONFIGURATION_STRINGS.jobCancelSuccessToastTitle),
      latestVersion: t(CONFIGURATION_STRINGS.latestVersion),
      noAvailableJobExecutions: t(CONFIGURATION_STRINGS.noAvailableJobExecutions),
      queued: t(CONFIGURATION_STRINGS.queued),
      reasonForTerminatingJobExecution: t(CONFIGURATION_STRINGS.reasonForTerminatingJobExecution),
      running: t(CONFIGURATION_STRINGS.running),
      started: t(CONFIGURATION_STRINGS.started),
      status: t(CONFIGURATION_STRINGS.status),
      terminated: t(CONFIGURATION_STRINGS.terminated),
    };
  }, [t]);

  const name = getSlug();

  // Page token object for pagination
  const pageTokens = useMemo(() => {
    const pageTokens: ApiTablePageTokens = {};

    if (jobExecutions && jobExecutions.links) {
      pageTokens.pageAfter = getPageToken(jobExecutions.links, JOB_EXECUTIONS_LIST_LINK_NEXT);
      pageTokens.pageBefore = getPageToken(jobExecutions.links, JOB_EXECUTIONS_LIST_LINK_PREVIOUS);
    }

    return pageTokens;
  }, [jobExecutions]);

  // Retrieve job executions
  const getJobExecutions = useCallback(
    (options: ApiTableFetchOptions) => {
      const { eventType, pageSize } = options;

      setIsLoading(true);

      let pageBefore: string | undefined;
      let pageAfter: string | undefined;

      if (eventType === API_TABLE_EVENT_PAGE_PREVIOUS) {
        pageBefore = pageTokens.pageBefore;
      }

      if (eventType === API_TABLE_EVENT_PAGE_NEXT) {
        pageAfter = pageTokens.pageAfter;
      }

      const dto: JobRunnerServiceGetJobExecutionsDto = {
        name,
        pageAfter,
        pageBefore,
        pageSize,
      };

      lastValueFrom(jobRunnerService.getJobExecutions(dto))
        .then((response) => {
          setJobExecutions(response as EntityJobExecutions);
          setIsLoading(false);
          return true;
        })
        .catch((e) => {
          // Just show regular empty message
          setJobExecutions({ data: [] });
          setIsLoading(false);
        });
    },
    [jobRunnerService, name, pageTokens, setIsLoading, setJobExecutions],
  );

  const refreshData = useCallback(() => {
    getJobExecutions({ eventType: 'pageSize', pageIndex: 1, pageSize: 20 });
  }, [getJobExecutions]);

  const terminateJobExecution = useCallback(
    (executionId: string): void => {
      const reason = labels.reasonForTerminatingJobExecution;
      const dto: JobRunnerServiceTerminateExecutionDto = {
        executionId,
        jobName: name,
        reason,
      };

      const successToastProps: ToastProps = {
        showToast: true,
        stateHandler: useToastStore,
        title: labels.jobCancelSuccessToastTitle,
        toastContent: (
          <span>
            {labels.jobCancelSuccessToastDescription1}
            <i> {executionId} </i>
            {labels.jobCancelSuccessToastDescription2}
          </span>
        ),
        toastType: TOAST_TYPE_SUCCESS,
      };

      const failToastProps: ToastProps = {
        showToast: true,
        stateHandler: useToastStore,
        title: labels.jobCancelFailToastTitle,
        toastContent: (
          <span>
            {labels.jobCancelFailToastDescription1}
            <i> {executionId} </i>
            {labels.jobCancelFailToastDescription2}
          </span>
        ),
        toastType: TOAST_TYPE_DANGER,
      };

      lastValueFrom(jobRunnerService.terminateJobExecution(dto))
        .then((_response) => {
          refreshData();

          updateActiveToast(null);
          updateActiveToast(<Toast {...successToastProps} />);
          return;
        })

        .catch((_err) => {
          updateActiveToast(null);
          updateActiveToast(<Toast {...failToastProps} />);
          return;
        });
    },
    [jobRunnerService, labels, name, refreshData, updateActiveToast],
  );

  const tableColumns: Array<TableCell> = [
    {
      content: labels.executionId,
      key: `executionId`,
    },
    {
      content: labels.latestVersion,
      key: `version`,
    },
    {
      content: labels.context,
      key: `context`,
    },
    {
      content: labels.status,
      key: `status`,
    },
    {
      content: labels.queued,
      key: `queued`,
    },
    {
      content: labels.started,
      key: `started`,
    },
    {
      content: labels.finished,
      key: `finished`,
    },
    {
      content: '',
      key: `terminateJob`,
    },
  ];

  const tableRows = useMemo(() => {
    if (!jobExecutions) {
      return [];
    }

    return jobExecutions.data.map((jobExecution: EntityJobExecution) => {
      const { id, status, timeFinished, timeQueued, timeStarted, context, version } = jobExecution;
      let updatedStatusString = '';
      let terminateJob: JSX.Element | null = null;

      if (status === labels.running) {
        terminateJob = (
          <div className="job__cancel-button-div">
            <Tooltip label={labels.cancelJob} position={TOOLTIP_POSITION_LEFT}>
              <Button
                classes="job__cancel-button"
                format={BUTTON_FORMAT_TRANSPARENT}
                icon={ICON_CANCEL_HOLLOW}
                onClick={(): void => terminateJobExecution(id)}
                theme={BUTTON_THEME_DEFAULT}
              />
            </Tooltip>
          </div>
        );
      }

      if (status === labels.terminated) {
        updatedStatusString = labels.cancelled;
      } else {
        updatedStatusString = getFormattedStatus(status);
      }

      const timestampDiv = (timestamp: string): JSX.Element => {
        return (
          <>
            {getFormattedDate(timestamp)},
            <br />
            {getFormattedTime(timestamp)}
          </>
        );
      };

      const jobVersionDiv = <span className="job-version">{version}</span>;

      return {
        context: context,
        executionId: <div className="execution-id">{id}</div>,
        finished: timeFinished ? timestampDiv(timeFinished) : '-',
        queued: timeQueued ? timestampDiv(timeQueued) : '-',
        started: timeStarted ? timestampDiv(timeStarted) : '-',
        status: updatedStatusString,
        terminateJob,
        version: jobVersionDiv,
      };
    });
  }, [jobExecutions, labels.cancelJob, labels.cancelled, labels.running, labels.terminated, terminateJobExecution]);

  if (jobExecutions && jobExecutions.data.length === 0) {
    const emptyStateClass = `sds-empty-state-card--with-cta`;
    const emptyStateProps: EmptyStateProps = {
      caption: labels.noAvailableJobExecutions,
      children: <StartJob name={name} refreshData={refreshData} />,
      classes: emptyStateClass,
      dataId: JOB_EXECUTIONS_DATA_IDS.noJobExecutionsFound,
      svg: ICON_INFO_WITH_LIST,
    };

    return (
      <div>
        <EmptyState {...emptyStateProps} />
      </div>
    );
  }

  const tableContent: TableContent = { columns: tableColumns, rows: tableRows };
  const tableProps: ApiTableProps = {
    classes: `job-executions-table`,
    fetchData: getJobExecutions,
    loading: isLoading,
    pageTokens,
    perPageDefault: 20,
    perPageOptions: [20, 50, 100],
    tableContent,
  };

  const getExecutionButtons = (): JSX.Element => {
    /* istanbul ignore next */
    if (!jobExecutions || jobExecutions.data.length === 0) {
      return <></>;
    }

    return (
      <div className="job__start-refresh-buttons-container">
        {<StartJob name={name} refreshData={refreshData} />}
        <Button
          classes="refresh-executions__button"
          data-id={JOB_EXECUTIONS_DATA_IDS.refreshJobExecutionsTable}
          format={BUTTON_FORMAT_SOFT}
          icon={ICON_REFRESH}
          onClick={(): void => refreshData()}
        />
      </div>
    );
  };

  return (
    <div>
      {getExecutionButtons()}
      <ApiTable {...tableProps} />
      {!jobExecutions && (
        <div className="loading">
          <Spinner type={SPINNER_TYPE_REGULAR} />
        </div>
      )}
    </div>
  );
};
